/**
 * Cloud upload/create modal component for OpenStack configurations.
 */
import React, { useState } from 'react';
import {
  uploadCloudYaml,
  createCloud,
  testCloudConnection,
  OpenStackCloudCreate,
  OpenStackCloud,
  CloudYamlUploadResponse,
} from '../api/openstack';
import './CloudUploadModal.css';

interface CloudUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (uploadResponse: CloudYamlUploadResponse) => void;
}

type AuthType = 'application_credential' | 'password';
type UploadMode = 'file' | 'manual';

export const CloudUploadModal: React.FC<CloudUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [mode, setMode] = useState<UploadMode>('file');
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [testedCloudId, setTestedCloudId] = useState<number | null>(null);

  // File upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Manual creation state
  const [formData, setFormData] = useState<OpenStackCloudCreate>({
    cloud_name: '',
    auth_type: 'application_credential',
    auth_url: '',
    region_name: '',
    interface: 'public',
    identity_api_version: '3',
  });

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setError(null);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      // Upload the file - don't wait for refresh to complete
      const uploadResponse = await uploadCloudYaml(selectedFile);
      // Pass upload results to parent and close immediately
      onSuccess(uploadResponse);
      onClose();
      resetForm();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to upload cloud.yaml file';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
    setTestResult(null);
  };

  const handleAuthTypeChange = (authType: AuthType) => {
    setFormData((prev) => ({
      ...prev,
      auth_type: authType,
      // Clear auth-specific fields when switching
      application_credential_id: undefined,
      application_credential_secret: undefined,
      username: undefined,
      password: undefined,
      project_id: undefined,
    }));
    setError(null);
    setTestResult(null);
  };

  const validateForm = (): boolean => {
    if (!formData.cloud_name.trim()) {
      setError('Cloud name is required');
      return false;
    }
    if (!formData.auth_url.trim()) {
      setError('Auth URL is required');
      return false;
    }

    if (formData.auth_type === 'application_credential') {
      if (!formData.application_credential_id?.trim()) {
        setError('Application Credential ID is required');
        return false;
      }
      if (!formData.application_credential_secret?.trim()) {
        setError('Application Credential Secret is required');
        return false;
      }
    } else if (formData.auth_type === 'password') {
      if (!formData.username?.trim()) {
        setError('Username is required');
        return false;
      }
      if (!formData.password?.trim()) {
        setError('Password is required');
        return false;
      }
      if (!formData.project_id?.trim()) {
        setError('Project ID is required for password authentication');
        return false;
      }
    }

    return true;
  };

  const handleTestConnection = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setTesting(true);
      setError(null);
      setTestResult(null);

      // Create a cloud to test (backend requires cloud_id to test)
      const cloud = await createCloud(formData);
      setTestedCloudId(cloud.id);
      const result = await testCloudConnection(cloud.id);

      if (result.success) {
        setTestResult('Connection test successful! You can now save this configuration.');
      } else {
        setError('Connection test failed. Please check your credentials.');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Connection test failed';
      setError(errorMessage);
    } finally {
      setTesting(false);
    }
  };

  const handleManualCreate = async () => {
    // If we already created a cloud during testing, don't create it again
    if (testedCloudId !== null) {
      // Cloud was already created during test, just close and refresh
      // Create a minimal response for consistency - parent will reload clouds anyway
      const response: CloudYamlUploadResponse = {
        clouds: [],
        added_count: 1,
        duplicate_count: 0,
        rejected_count: 0,
        rejected_clouds: [],
        message: 'Cloud configuration saved successfully',
      };
      onSuccess(response);
      onClose();
      resetForm();
      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const cloud = await createCloud(formData);
      // Create proper response structure for consistency with file upload
      const response: CloudYamlUploadResponse = {
        clouds: [cloud],
        added_count: 1,
        duplicate_count: 0,
        rejected_count: 0,
        rejected_clouds: [],
        message: 'Cloud configuration created successfully',
      };
      onSuccess(response);
      onClose();
      resetForm();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create cloud configuration';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setFormData({
      cloud_name: '',
      auth_type: 'application_credential',
      auth_url: '',
      region_name: '',
      interface: 'public',
      identity_api_version: '3',
    });
    setError(null);
    setTestResult(null);
    setTestedCloudId(null);
    setMode('file');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Add OpenStack Cloud</h2>
          <button className="modal-close" onClick={handleClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-tabs">
          <button
            className={`modal-tab ${mode === 'file' ? 'active' : ''}`}
            onClick={() => {
              setMode('file');
              setError(null);
            }}
          >
            Upload File
          </button>
          <button
            className={`modal-tab ${mode === 'manual' ? 'active' : ''}`}
            onClick={() => {
              setMode('manual');
              setError(null);
            }}
          >
            Manual Entry
          </button>
        </div>

        {error && (
          <div className="error-message" role="alert">
            <strong>Error:</strong> {error}
          </div>
        )}

        {testResult && (
          <div className="success-message" role="alert">
            {testResult}
          </div>
        )}

        {mode === 'file' ? (
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="cloud-yaml-file">Select cloud.yaml file</label>
              <input
                id="cloud-yaml-file"
                type="file"
                accept=".yaml,.yml"
                onChange={handleFileChange}
                disabled={loading}
              />
              <small className="form-hint">
                Upload a cloud.yaml file containing one or more OpenStack cloud configurations
              </small>
            </div>

            <div className="modal-actions">
              <button className="btn-cancel" onClick={handleClose} disabled={loading}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleFileUpload}
                disabled={loading || !selectedFile}
              >
                {loading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        ) : (
          <div className="modal-body">
            <div className="form-group">
              <label htmlFor="cloud-name">Cloud Name *</label>
              <input
                id="cloud-name"
                type="text"
                name="cloud_name"
                value={formData.cloud_name}
                onChange={handleInputChange}
                placeholder="My OpenStack Cloud"
                disabled={loading || testing}
                required
              />
              <small className="form-hint">A friendly name for this cloud configuration</small>
            </div>

            <div className="form-group">
              <label htmlFor="auth-url">Auth URL *</label>
              <input
                id="auth-url"
                type="url"
                name="auth_url"
                value={formData.auth_url}
                onChange={handleInputChange}
                placeholder="https://openstack.example.com:5000/v3"
                disabled={loading || testing}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="region-name">Region Name</label>
              <input
                id="region-name"
                type="text"
                name="region_name"
                value={formData.region_name || ''}
                onChange={handleInputChange}
                placeholder="RegionOne"
                disabled={loading || testing}
              />
            </div>

            <div className="form-group">
              <label htmlFor="auth-type">Authentication Type *</label>
              <select
                id="auth-type"
                name="auth_type"
                value={formData.auth_type}
                onChange={(e) => handleAuthTypeChange(e.target.value as AuthType)}
                disabled={loading || testing}
              >
                <option value="application_credential">Application Credential</option>
                <option value="password">Password</option>
              </select>
            </div>

            {formData.auth_type === 'application_credential' ? (
              <>
                <div className="form-group">
                  <label htmlFor="app-cred-id">Application Credential ID *</label>
                  <input
                    id="app-cred-id"
                    type="text"
                    name="application_credential_id"
                    value={formData.application_credential_id || ''}
                    onChange={handleInputChange}
                    placeholder="app-cred-id-123"
                    disabled={loading || testing}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="app-cred-secret">Application Credential Secret *</label>
                  <input
                    id="app-cred-secret"
                    type="password"
                    name="application_credential_secret"
                    value={formData.application_credential_secret || ''}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                    disabled={loading || testing}
                    required
                  />
                </div>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label htmlFor="username">Username *</label>
                  <input
                    id="username"
                    type="text"
                    name="username"
                    value={formData.username || ''}
                    onChange={handleInputChange}
                    placeholder="myuser"
                    disabled={loading || testing}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="password">Password *</label>
                  <input
                    id="password"
                    type="password"
                    name="password"
                    value={formData.password || ''}
                    onChange={handleInputChange}
                    placeholder="••••••••"
                    disabled={loading || testing}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="project-id">Project ID *</label>
                  <input
                    id="project-id"
                    type="text"
                    name="project_id"
                    value={formData.project_id || ''}
                    onChange={handleInputChange}
                    placeholder="project-123"
                    disabled={loading || testing}
                    required
                  />
                </div>
              </>
            )}

            <div className="modal-actions">
              <button className="btn-cancel" onClick={handleClose} disabled={loading || testing}>
                Cancel
              </button>
              <button
                className="btn-secondary"
                onClick={handleTestConnection}
                disabled={loading || testing}
              >
                {testing ? 'Testing...' : 'Test Connection'}
              </button>
              <button
                className="btn-primary"
                onClick={handleManualCreate}
                disabled={loading || testing}
              >
                {testedCloudId !== null
                  ? 'Save'
                  : loading
                  ? 'Creating...'
                  : 'Create'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

